import { Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Product } from '../models/product.model';

@Injectable({ providedIn: 'root' })
export class SeoService {

    constructor(@Inject(DOCUMENT) private document: Document, private meta: Meta, private title: Title) { }

    setStaticPageSEO(options: { title: string; description: string; path: string; keywords?: string }) {
        const { title, description, path, keywords } = options;
        const url = `${this.document.location.origin}${path}`;

        this.title.setTitle(title);
        this.meta.updateTag({ name: 'description', content: description });

        if (keywords) {
            this.meta.updateTag({ name: 'keywords', content: keywords });
        }

        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:url', content: url });

        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: title });
        this.meta.updateTag({ name: 'twitter:description', content: description });

        this.setCanonicalUrl(url);
    }

    /** Canonical for an app path, using the production origin so staging/preview hosts never become canonical. */
    setCanonicalPath(path: string) {
        const origin = this.document.location?.origin || '';
        const canonicalOrigin = /localhost|staging|netlify/.test(origin) ? 'https://thenearbyshop.com' : origin;
        this.setCanonicalUrl(`${canonicalOrigin}${path}`);
        this.meta.updateTag({ property: 'og:url', content: `${canonicalOrigin}${path}` });
    }

    setCanonicalUrl(url?: string) {
        const canonicalUrl = url || this.document.location.href;

        let link: HTMLLinkElement =
            this.document.querySelector("link[rel='canonical']") ||
            this.document.createElement('link');

        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', canonicalUrl);

        if (!link.parentNode) {
            this.document.head.appendChild(link);
        }
    }
    setProductSEO(product: Product) {
        if (!product) return;

        const title =
            product.seo_title || `${product.name} | Buy Online Near You`;

        const description =
            product.seo_description ||
            product.description ||
            '';

        const keywords = product.seo_keywords || '';
        const image = product.seo_image || '';
        const url = `${this.document.location.origin}/product/${product.slug}`;

        // 🔥 TITLE
        this.title.setTitle(title);

        // 🔥 BASIC META
        this.meta.updateTag({ name: 'description', content: description });

        if (keywords) {
            this.meta.updateTag({ name: 'keywords', content: keywords });
        }

        // 🔥 OPEN GRAPH
        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'product' });
        this.meta.updateTag({ property: 'og:url', content: url });

        if (image) {
            this.meta.updateTag({ property: 'og:image', content: image });
        }

        // 🔥 TWITTER
        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: title });
        this.meta.updateTag({ name: 'twitter:description', content: description });

        if (image) {
            this.meta.updateTag({ name: 'twitter:image', content: image });
        }
    }
    setProductSchema(product: Product) {
        if (!product) return;

        const p: any = product;
        const image = p.seo_image || p.image;
        const price = p.final_price ?? p.price;
        const shopName = typeof p.shop === 'object' ? p.shop?.name : p.shop;
        const schema: any = {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": p.seo_title || p.name,
            "description": p.seo_description || p.description,
            "image": image ? [image] : [],
            "sku": p.sku || p.id,
            "offers": {
                "@type": "Offer",
                "priceCurrency": "INR",
                "price": price,
                "availability": Number(p.stock) > 0
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                "url": `${this.document.location.origin}/product/${p.slug || p.id}`,
                ...(shopName ? { "seller": { "@type": "Organization", "name": shopName } } : {})
            }
        };
        // Brand is only emitted when the API provides one - the product name is not a brand.
        if (p.brand) {
            schema.brand = { "@type": "Brand", "name": typeof p.brand === 'object' ? p.brand.name : p.brand };
        }

        // 🔥 Remove existing schema to avoid duplicates
        const existingScript = this.document.getElementById('product-schema');
        if (existingScript) {
            existingScript.remove();
        }

        // 🔥 Inject fresh schema
        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'product-schema';
        script.text = JSON.stringify(schema);

        this.document.head.appendChild(script);
    }
    setVendorSEO(vendor: any) {
        if (!vendor) return;

        const title = `${vendor.name} | TheNearByShop`;

        const description =
            vendor.description ||
            `Shop online from ${vendor.name}. Order groceries, food, and daily essentials near you.`;

        const image = vendor.banner || vendor.shop_image || '';
        const url = `${this.document.location.origin}/shop/${vendor.slug}`;

        // 🔥 TITLE
        this.title.setTitle(title);

        // 🔥 BASIC META
        this.meta.updateTag({ name: 'description', content: description });

        // 🔥 OPEN GRAPH (WhatsApp uses this)
        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ property: 'og:description', content: description });
        this.meta.updateTag({ property: 'og:type', content: 'website' });
        this.meta.updateTag({ property: 'og:url', content: url });

        if (image) {
            this.meta.updateTag({ property: 'og:image', content: image });
        }

        // 🔥 TWITTER
        this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
        this.meta.updateTag({ name: 'twitter:title', content: title });
        this.meta.updateTag({ name: 'twitter:description', content: description });

        if (image) {
            this.meta.updateTag({ name: 'twitter:image', content: image });
        }

        // 🔥 Canonical
        this.setCanonicalUrl(url);
    }

    setVendorSchema(vendor: any) {
        if (!vendor) return;

        const schema = {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": vendor.name,
            "description": vendor.description || "",
            "image": vendor.banner || vendor.shop_image || "",
            "url": this.document.location.href,
            "telephone": vendor.phone || "",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": vendor.address || "",
                "addressLocality": vendor.city || "",
                "addressRegion": vendor.state || "",
                "postalCode": vendor.pincode || "",
                "addressCountry": "IN"
            },
            "geo": vendor.latitude && vendor.longitude ? {
                "@type": "GeoCoordinates",
                "latitude": vendor.latitude,
                "longitude": vendor.longitude
            } : undefined
        };

        // Remove old schema
        const existingScript = this.document.getElementById('vendor-schema');
        if (existingScript) {
            existingScript.remove();
        }

        const script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'vendor-schema';
        script.text = JSON.stringify(schema);

        this.document.head.appendChild(script);
    }

}
